import {
  getUserNetPaidAmount,
  isActivityConfirmed,
  type UserPaymentStatus
} from '../../../../pure-js/libs/EnrollmentHelper'
import { isWalkInPayment } from '../../../../pure-js/libs/isWalkInPayment'
import { getPrimaryCheckinQrForEnrollment } from '../../../../pure-js/libs/getPrimaryCheckinQr'
import { findPeriodByAnyId } from '../../../../pure-js/libs/PeriodHelper'
import { buildRoute } from '../../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../../pure-js/libs/RoutePath'
import {
  Activity,
  Client,
  Enrollment,
  EnrollmentPaymentStatus,
  EnrollmentStatus,
  OccationInstance,
  PayFor,
  Payment,
  PriceDetails
} from '../../../../pure-js/types/GrooverTypes'

export const stripHtml = (html: string): string => html.replace(/<[^>]*>/g, '').trim()

export const hasEnrollmentDescription = (html: string | undefined): boolean => {
  if (!html?.trim()) return false
  if (stripHtml(html)) return true
  return /<img\b/i.test(html)
}

export const isWalkInOnlyEnrollment = (enrollment: Enrollment): boolean => {
  const payments = enrollment.payments ?? []
  const walkInPays = payments.filter(isWalkInPayment)
  if (walkInPays.length === 0) return false
  const otherPays = payments.filter((p) => p.type !== 'refund' && !isWalkInPayment(p))
  return otherPays.length === 0
}

export const isVisiblePortalEnrollment = (enrollment: Enrollment): boolean => {
  if (enrollment.status === EnrollmentStatus.Deleted) return false
  if (isWalkInOnlyEnrollment(enrollment)) return false
  return true
}

export const getPortalCourseEnrollments = (enrollments: Enrollment[] | undefined | null): Enrollment[] => {
  return (enrollments ?? [])
    .filter(isVisiblePortalEnrollment)
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
}

export const getEnrollmentPrimaryActivity = (enrollment: Enrollment): Activity | undefined => {
  return enrollment.activities?.[0]
}

/** Display fields that should follow the live activity, not the enrollment snapshot. */
export const overlayLiveActivityDisplay = (snapshot: Activity, live?: Activity | null): Activity => {
  if (!live) return snapshot
  return {
    ...snapshot,
    name: live.name,
    description: live.description,
    place: live.place,
    locationId: live.locationId,
    roomId: live.roomId,
    occations: live.occations,
    noOfOccasions: live.noOfOccasions,
    startDate: live.startDate,
    time: live.time,
    endDate: live.endDate,
    endTime: live.endTime,
    period: live.period,
    periodId: live.periodId
  }
}

export const applyLiveActivitiesToEnrollment = (
  enrollment: Enrollment,
  liveById: Record<string, Activity | null | undefined>
): Enrollment => {
  if (!enrollment.activities?.length) return enrollment
  let changed = false
  const activities = enrollment.activities.map((snapshot) => {
    const live = liveById[snapshot.id]
    if (!live) return snapshot
    changed = true
    return overlayLiveActivityDisplay(snapshot, live)
  })
  return changed ? { ...enrollment, activities } : enrollment
}

export const getEnrollmentActivityIds = (enrollments: Enrollment[]): string[] => [
  ...new Set(
    enrollments.flatMap((enrollment) => (enrollment.activities ?? []).map((activity) => activity.id)).filter(Boolean)
  )
]

export const getEnrollmentTitle = (enrollment: Enrollment, fallback: string): string => {
  const name = getEnrollmentPrimaryActivity(enrollment)?.name?.trim()
  return name || fallback
}

export const getEnrollmentLocationName = (activity: Activity | undefined, client: Client | null): string => {
  if (!activity) return ''
  if (activity.place?.trim()) return activity.place.trim()
  const location = client?.locations?.find((l) => l.id === activity.locationId)
  if (!location) return ''
  const room = location.rooms?.find((r) => r.id === activity.roomId)
  return room?.name ? `${location.name} · ${room.name}` : location.name
}

export const getEnrollmentOccasions = (activity: Activity | undefined): OccationInstance[] => {
  if (!activity?.occations?.length) return []
  return [...activity.occations].sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
}

const APPROVED_STATUSES = new Set<EnrollmentStatus>([EnrollmentStatus.Confirmed, EnrollmentStatus.Active])

export const isPortalEnrollmentApproved = (enrollment: Enrollment): boolean => {
  return APPROVED_STATUSES.has(enrollment.status)
}

export const isPortalEnrollmentPaid = (enrollment: Enrollment): boolean => {
  return enrollment.paymentStatus === EnrollmentPaymentStatus.Paid
}

export const canShowPortalEnrollmentQr = (enrollment: Enrollment, client?: Client | null): boolean => {
  if (isPortalEnrollmentPaid(enrollment)) return true
  return isPortalEnrollmentApproved(enrollment) && client?.showQrCodeBeforePayment === true
}

export const getEnrollmentQrValue = (enrollment: Enrollment, client?: Client | null): string | null => {
  if (!canShowPortalEnrollmentQr(enrollment, client)) return null
  return getPrimaryCheckinQrForEnrollment(enrollment)
}

export const getActivityPublicPath = (activity: Activity, client: Client | null, lang?: string): string => {
  const langQuery = lang ? { lang } : undefined
  if (client?.slug && activity.seoLink) {
    return buildRoute(
      RoutePath.ACTIVITY_DETAIL_SEO,
      { clientSlug: client.slug, activitySlug: activity.seoLink },
      langQuery
    )
  }
  return buildRoute(RoutePath.ACTIVITY_DETAIL, { activityId: activity.id }, langQuery)
}

const parseActivityDateTime = (value: string): Date | null => {
  const normalized = value.includes('T') ? value : value.replace(' ', 'T')
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

export const getActivityStartDateTime = (activity: Activity): Date | null => {
  const firstOccasion = getEnrollmentOccasions(activity)[0]
  if (firstOccasion?.startDate) {
    const fromOccasion = parseActivityDateTime(firstOccasion.startDate)
    if (fromOccasion) return fromOccasion
  }
  if (!activity.startDate) return null
  return parseActivityDateTime(activity.time ? `${activity.startDate} ${activity.time}` : activity.startDate)
}

export const getNextOccasionDateTime = (activity: Activity, now = new Date()): Date | null => {
  const upcoming = getEnrollmentOccasions(activity)
    .map((occasion) => parseActivityDateTime(occasion.startDate))
    .filter((date): date is Date => !!date && date.getTime() >= now.getTime())
    .sort((a, b) => a.getTime() - b.getTime())
  if (upcoming[0]) return upcoming[0]
  const start = getActivityStartDateTime(activity)
  return start && start.getTime() >= now.getTime() ? start : null
}

export type RelativeUpcomingUnit = 'days' | 'hours' | 'minutes'

export const getRelativeUpcomingUnit = (
  target: Date,
  now = new Date()
): { value: number; unit: RelativeUpcomingUnit } | null => {
  const ms = target.getTime() - now.getTime()
  if (ms < 0) return null
  const minutes = Math.round(ms / 60_000)
  if (minutes < 60) return { value: Math.max(minutes, 0), unit: 'minutes' }
  const hours = Math.round(ms / 3_600_000)
  if (hours < 24) return { value: hours, unit: 'hours' }
  return { value: Math.round(ms / 86_400_000), unit: 'days' }
}

export const formatActivityStartDate = (date: Date, locale: string): string =>
  date.toLocaleDateString(locale, { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })

export const formatActivityStartTime = (date: Date, locale: string): string =>
  date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })

export type PortalPriceLine = {
  activityId: string
  activityName: string
  originalPrice: number
  totalDiscountPrice: number
  finalPrice: number
  administrationFee: number
  discountNames: string[]
}

export const getEnrollmentActivityStatus = (enrollment: Enrollment, activityId: string): EnrollmentStatus => {
  const activityStatus = enrollment.statuses?.find((entry) => entry.activityId === activityId)
  return activityStatus?.status ?? enrollment.status
}

const isApprovedActivityStatus = (status: EnrollmentStatus): boolean => APPROVED_STATUSES.has(status)

const getAppliesToForPayFor = (payFor: PayFor): string => (payFor === PayFor.Partner ? 'partnerUser' : 'mainUser')

const getActivityPriceLinesForPayFor = (enrollment: Enrollment, activityId: string, payFor: PayFor): PriceDetails[] => {
  const appliesTo = getAppliesToForPayFor(payFor)
  return (enrollment.priceDetails ?? []).filter((pd) => pd.activityId === activityId && pd.appliesTo === appliesTo)
}

const getActivityOwedForPayFor = (enrollment: Enrollment, activityId: string, payFor: PayFor): number =>
  getActivityPriceLinesForPayFor(enrollment, activityId, payFor).reduce((sum, pd) => sum + (pd.finalPrice ?? 0), 0)

/** Payment status for one activity, or null when it is not yet approved to bill. */
export const getPortalActivityPaymentStatus = (
  enrollment: Enrollment,
  activityId: string,
  payFor: PayFor
): EnrollmentPaymentStatus | null => {
  if (!isApprovedActivityStatus(getEnrollmentActivityStatus(enrollment, activityId))) return null

  if (enrollment.paymentStatus === EnrollmentPaymentStatus.Paid) {
    return EnrollmentPaymentStatus.Paid
  }

  const priceLines = getActivityPriceLinesForPayFor(enrollment, activityId, payFor)
  if (priceLines.length === 0) return EnrollmentPaymentStatus.Unpaid

  const owed = getActivityOwedForPayFor(enrollment, activityId, payFor)
  if (owed <= 0) return EnrollmentPaymentStatus.Paid

  const payableActivities = (enrollment.activities ?? []).filter((activity) =>
    isApprovedActivityStatus(getEnrollmentActivityStatus(enrollment, activity.id))
  )
  const netPaid = getUserNetPaidAmount({ ...enrollment, payments: enrollment.payments ?? [] }, payFor)

  let remaining = netPaid
  for (const activity of payableActivities) {
    const activityOwed = getActivityOwedForPayFor(enrollment, activity.id, payFor)
    const allocated = Math.min(remaining, activityOwed)
    remaining -= allocated
    if (activity.id !== activityId) continue
    if (allocated >= activityOwed) return EnrollmentPaymentStatus.Paid
    if (allocated > 0) return EnrollmentPaymentStatus.PartlyPaid
    return EnrollmentPaymentStatus.Unpaid
  }

  return EnrollmentPaymentStatus.Unpaid
}

export const isPortalActivityPaid = (enrollment: Enrollment, activityId: string, payFor: PayFor): boolean =>
  getPortalActivityPaymentStatus(enrollment, activityId, payFor) === EnrollmentPaymentStatus.Paid

/** Confirmed total, net paid, and leftover — leftover matches register `getToBePaidData`. */
export const getPortalPaymentTotals = (
  paymentStatus: Pick<UserPaymentStatus, 'due' | 'paid' | 'balance'>
): { total: number; paid: number; remaining: number } => ({
  total: Math.max(0, paymentStatus.due),
  paid: Math.max(0, paymentStatus.paid),
  remaining: Math.max(0, paymentStatus.balance)
})

export const getPortalPriceLines = (enrollment: Enrollment, payFor: PayFor): PortalPriceLine[] => {
  const appliesTo = payFor === PayFor.Partner ? 'partnerUser' : 'mainUser'
  return (enrollment.priceDetails ?? [])
    .filter((pd: PriceDetails) => pd.appliesTo === appliesTo && isActivityConfirmed(enrollment, pd.activityId))
    .map((pd) => ({
      activityId: pd.activityId,
      activityName: enrollment.activities?.find((activity) => activity.id === pd.activityId)?.name || pd.activityId,
      originalPrice: pd.originalPrice ?? pd.finalPrice,
      totalDiscountPrice: pd.totalDiscountPrice ?? 0,
      finalPrice: pd.finalPrice,
      administrationFee: pd.administrationFee ?? 0,
      discountNames: (pd.discounts ?? []).map((discount) => discount.name).filter(Boolean)
    }))
}

export const getPaymentStatusTextKey = (status: EnrollmentPaymentStatus | undefined): string => {
  switch (status) {
    case EnrollmentPaymentStatus.Paid:
      return 'paymentStatus.paid'
    case EnrollmentPaymentStatus.PartlyPaid:
      return 'paymentStatus.partlyPaid'
    case EnrollmentPaymentStatus.Refunded:
      return 'paymentStatus.refunded'
    case EnrollmentPaymentStatus.PartlyRefunded:
      return 'paymentStatus.partlyRefunded'
    default:
      return 'paymentStatus.unpaid'
  }
}

export const getPortalReceiptPayments = (enrollment: Enrollment): Payment[] =>
  [...(enrollment.payments ?? []).filter((payment) => payment.type !== 'refund')].sort(
    (a, b) => new Date(b.paymentDate ?? 0).getTime() - new Date(a.paymentDate ?? 0).getTime()
  )

export const getPortalReceiptRowKey = (payment: Payment, index: number): string => payment.paymentRef || `pay-${index}`

export const hasPortalEnrollmentReceipt = (enrollment: Enrollment): boolean =>
  getPortalReceiptPayments(enrollment).length > 0

const PAYABLE_ENROLLMENT_STATUSES = new Set<EnrollmentStatus>([
  EnrollmentStatus.Confirmed,
  EnrollmentStatus.Active,
  EnrollmentStatus.PartlyApproved
])

const RESUMABLE_PAYMENT_STATUSES = new Set<EnrollmentPaymentStatus>([
  EnrollmentPaymentStatus.Unpaid,
  EnrollmentPaymentStatus.PartlyPaid
])

export const canResumePortalEnrollmentPayment = (enrollment: Enrollment): boolean => {
  if (!PAYABLE_ENROLLMENT_STATUSES.has(enrollment.status)) return false
  const paymentStatus = enrollment.paymentStatus ?? EnrollmentPaymentStatus.Unpaid
  if (!RESUMABLE_PAYMENT_STATUSES.has(paymentStatus)) return false
  return !!(enrollment.activityIds?.[0] ?? enrollment.activities?.[0]?.id)
}

export const canShowPortalEnrollmentPayNow = (enrollment: Enrollment): boolean =>
  canResumePortalEnrollmentPayment(enrollment)

const trimPartnerDisplayName = (name?: string): string | null => {
  const trimmed = name?.trim()
  return trimmed || null
}

export const getPortalHardCouplePartnerName = (
  enrollment: Enrollment,
  viewerEnrollmentUserId?: string
): string | null => {
  if (!enrollment.partnerUser && !enrollment.enrollmentGroupId) return null

  if (enrollment.partnerUser) {
    const viewerIsPartner = !!viewerEnrollmentUserId && enrollment.partnerUser.id === viewerEnrollmentUserId
    return trimPartnerDisplayName(viewerIsPartner ? enrollment.mainUser?.name : enrollment.partnerUser.name)
  }

  const registeredPartners =
    enrollment.discountUserInput && 'registeredPartners' in enrollment.discountUserInput
      ? enrollment.discountUserInput.registeredPartners
      : undefined
  const firstPartner = registeredPartners?.[0]
  return trimPartnerDisplayName(firstPartner?.linkedUserName || firstPartner?.enteredName)
}

export const getPortalEnrollmentChildNames = (enrollment: Enrollment, activityId?: string): string | null => {
  const children = enrollment.mainUser?.children ?? []
  if (children.length === 0) return null

  const assignedIds = activityId
    ? (enrollment.childrenByActivity?.[activityId] ?? [])
    : [...new Set(Object.values(enrollment.childrenByActivity ?? {}).flat())]
  if (assignedIds.length === 0) return null

  const byId = new Map(children.map((child) => [child.id, child]))
  const names = assignedIds.map((id) => byId.get(id)?.name?.trim()).filter((name): name is string => Boolean(name))
  return names.length > 0 ? names.join(', ') : null
}

export const handlePortalPayNowClick = (
  event: { preventDefault: () => void; stopPropagation: () => void },
  isViewAs: boolean
): boolean => {
  event.stopPropagation()
  if (!isViewAs) return true
  event.preventDefault()
  return false
}

export const getPortalEnrollmentPaymentPath = (
  enrollment: Enrollment,
  options?: { enrollmentUserId?: string; lang?: string }
): string | null => {
  const activityId = enrollment.activityIds?.[0] ?? enrollment.activities?.[0]?.id
  if (!activityId || !enrollment.id) return null
  const query: Record<string, string> = {}
  if (options?.lang) query.lang = options.lang
  if (options?.enrollmentUserId) query.u = options.enrollmentUserId
  return buildRoute(
    RoutePath.REGISTER_OBFUSCATED,
    { activityId, enrollmentId: enrollment.id },
    Object.keys(query).length > 0 ? query : undefined
  )
}

export const getPayForForPortalUser = (enrollment: Enrollment, enrollmentUserId: string): PayFor => {
  if (enrollment.partnerUser?.id === enrollmentUserId) return PayFor.Partner
  return PayFor.Main
}

const STATUS_KEYS: Record<EnrollmentStatus, string> = {
  [EnrollmentStatus.Pending]: 'portalEnrollmentStatusPending',
  [EnrollmentStatus.Confirmed]: 'portalEnrollmentStatusConfirmed',
  [EnrollmentStatus.Active]: 'portalEnrollmentStatusActive',
  [EnrollmentStatus.Cancelled]: 'portalEnrollmentStatusCancelled',
  [EnrollmentStatus.Deleted]: 'portalEnrollmentStatusCancelled',
  [EnrollmentStatus.Refunded]: 'portalEnrollmentStatusCancelledWithRefund',
  [EnrollmentStatus.PartlyApproved]: 'portalEnrollmentStatusPartlyApproved',
  [EnrollmentStatus.ClassCancelled]: 'portalEnrollmentStatusClassCancelled',
  [EnrollmentStatus.CancelledWithRefund]: 'portalEnrollmentStatusCancelledWithRefund',
  [EnrollmentStatus.CancelledWithCredit]: 'portalEnrollmentStatusCancelledWithCredit'
}

export const getEnrollmentStatusLabel = (status: EnrollmentStatus, getText: (key: string) => string): string => {
  const key = STATUS_KEYS[status]
  return key ? getText(key) : status
}

export const formatOccasionDateTime = (startDate: string, locale: string): string => {
  const date = new Date(startDate)
  if (Number.isNaN(date.getTime())) return startDate
  return date.toLocaleString(locale, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export const NO_PERIOD_GROUP_KEY = 'none'

export type PortalEnrollmentPeriodGroup = {
  periodKey: string
  periodName: string
  sortDate: string
  enrollments: Enrollment[]
}

export type PortalEnrollmentSchoolGroup = {
  schoolKey: string
  schoolName: string
  periods: PortalEnrollmentPeriodGroup[]
}

export const getEnrollmentPeriodGroup = (
  enrollment: Enrollment,
  client: Client | null | undefined,
  noPeriodLabel: string
): Pick<PortalEnrollmentPeriodGroup, 'periodKey' | 'periodName' | 'sortDate'> => {
  const activity = getEnrollmentPrimaryActivity(enrollment)
  const period = findPeriodByAnyId(client?.periods, activity?.periodId)
  const legacy = activity?.period?.trim()
  // Key comes from the activity so groups do not remount when client.periods load.
  if (activity?.periodId) {
    return {
      periodKey: `id:${activity.periodId}`,
      periodName: period?.periodName || legacy || noPeriodLabel,
      sortDate: period?.periodStart || activity.startDate || ''
    }
  }
  if (legacy) {
    return {
      periodKey: `legacy:${legacy}`,
      periodName: legacy,
      sortDate: activity?.startDate || ''
    }
  }
  return {
    periodKey: NO_PERIOD_GROUP_KEY,
    periodName: noPeriodLabel,
    sortDate: activity?.startDate || ''
  }
}

const comparePeriodGroups = (a: PortalEnrollmentPeriodGroup, b: PortalEnrollmentPeriodGroup): number => {
  if (a.periodKey === NO_PERIOD_GROUP_KEY && b.periodKey !== NO_PERIOD_GROUP_KEY) return 1
  if (b.periodKey === NO_PERIOD_GROUP_KEY && a.periodKey !== NO_PERIOD_GROUP_KEY) return -1
  const aTime = a.sortDate ? new Date(a.sortDate).getTime() : 0
  const bTime = b.sortDate ? new Date(b.sortDate).getTime() : 0
  if (!Number.isNaN(aTime) && !Number.isNaN(bTime) && aTime !== bTime) return bTime - aTime
  return a.periodName.localeCompare(b.periodName, undefined, { sensitivity: 'base' })
}

export const groupPortalEnrollmentsBySchoolAndPeriod = (
  enrollments: Enrollment[],
  clients: Record<string, Client | null>,
  labels: { unknownSchool: string; noPeriod: string }
): PortalEnrollmentSchoolGroup[] => {
  const schoolMap = new Map<string, { schoolName: string; periodMap: Map<string, PortalEnrollmentPeriodGroup> }>()

  for (const enrollment of enrollments) {
    const schoolKey = enrollment.clientId || 'unknown'
    const client = clients[enrollment.clientId] ?? null
    const resolvedSchoolName = client?.name?.trim()
    const period = getEnrollmentPeriodGroup(enrollment, client, labels.noPeriod)

    let school = schoolMap.get(schoolKey)
    if (!school) {
      school = { schoolName: resolvedSchoolName || labels.unknownSchool, periodMap: new Map() }
      schoolMap.set(schoolKey, school)
    } else if (resolvedSchoolName) {
      school.schoolName = resolvedSchoolName
    }

    let periodGroup = school.periodMap.get(period.periodKey)
    if (!periodGroup) {
      periodGroup = { ...period, enrollments: [] }
      school.periodMap.set(period.periodKey, periodGroup)
    }
    periodGroup.enrollments.push(enrollment)
  }

  return [...schoolMap.entries()]
    .map(([schoolKey, school]) => ({
      schoolKey,
      schoolName: school.schoolName,
      periods: [...school.periodMap.values()].sort(comparePeriodGroups)
    }))
    .sort((a, b) => a.schoolName.localeCompare(b.schoolName, undefined, { sensitivity: 'base' }))
}

export const countSchoolEnrollments = (school: PortalEnrollmentSchoolGroup): number =>
  school.periods.reduce((sum, period) => sum + period.enrollments.length, 0)
